import React from 'react';
import { Box, Card, Container, Grid, Typography } from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ScienceIcon from '@mui/icons-material/Science';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BiotechIcon from '@mui/icons-material/Biotech';
import HealingIcon from '@mui/icons-material/Healing';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { normalizeBoxes } from '../../utils/servicePageBody';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const BOX_ICONS = [
  MedicalServicesIcon,
  LocalHospitalIcon,
  ScienceIcon,
  AccessTimeIcon,
  BiotechIcon,
  HealingIcon,
  MonitorHeartIcon,
];

const getGridSize = (count) => {
  if (count === 1) return { xs: 12, md: 8, lg: 6 };
  if (count === 2) return { xs: 12, md: 6 };
  if (count === 3) return { xs: 12, md: 4 };
  if (count === 4) return { xs: 12, md: 6 };
  return { xs: 12, sm: 6, md: 4, lg: 3 };
};

const RichTextContent = ({ html }) => {
  const value = String(html || '').trim();
  if (!value) return null;

  if (value.includes('<')) {
    return (
      <Box
        sx={{
          color: 'text.secondary',
          typography: 'body2',
          '& p': { m: 0 },
          '& ul, & ol': { pl: 2, my: 0.5 },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
      />
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {value}
    </Typography>
  );
};

const ServicePageBoxes = ({ boxesSectionTitle = '', boxes = [], clickable = false, onBoxClick, greyBackground = false }) => {
  const items = normalizeBoxes(boxes).filter((item) => item.title || item.text);
  const hasContent = items.length > 0;

  if (!hasContent && !boxesSectionTitle) return null;

  const gridSize = getGridSize(items.length);

  const content = (
    <Box sx={{ mb: greyBackground ? 0 : 8 }}>
      {boxesSectionTitle && (
        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          {boxesSectionTitle}
        </Typography>
      )}
      <Grid
        container
        spacing={4}
        justifyContent={items.length === 1 ? 'center' : 'flex-start'}
      >
        {items.map((item, index) => {
          const Icon = BOX_ICONS[index % BOX_ICONS.length];
          const isClickable = clickable && typeof onBoxClick === 'function';

          return (
            <Grid item {...gridSize} key={`service-box-${index}`}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  '&:hover': isClickable ? { transform: 'translateY(-8px)', boxShadow: 6 } : undefined,
                }}
                onClick={isClickable ? () => onBoxClick(index, item.title) : undefined}
              >
                <Icon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                {item.title && (
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {item.title}
                  </Typography>
                )}
                <RichTextContent html={item.text} />
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  if (greyBackground) {
    return (
      <Box sx={{ bgcolor: '#f5f5f5', py: 8, mb: 0 }}>
        <Container maxWidth="lg">
          {content}
        </Container>
      </Box>
    );
  }

  return content;
};

export default ServicePageBoxes;
